<template>
  <v-dialog
    :value="value"
    :fullscreen="isMobile"
    max-width="640"
    scrollable
    content-class="crm-dialog"
    @input="$emit('input', $event)"
  >
    <div class="cf">
      <header class="cf__head">
        <span class="cf__icon"><v-icon color="primary">mdi-account-plus-outline</v-icon></span>
        <div class="cf__titles">
          <div class="cf__title">{{ lead ? $t('crmEditLead') : $t('crmNewLead') }}</div>
          <div class="cf__sub">{{ $t('crmLeadFormHint') }}</div>
        </div>
        <button type="button" class="cf__close" @click="close"><v-icon>mdi-close</v-icon></button>
      </header>

      <div class="cf__body">
        <v-alert v-if="duplicates.length" type="warning" text dense class="cf__dup">
          <div class="cf__dup-title">{{ $t('crmDuplicateFound') }}</div>
          <div v-for="d in duplicates" :key="d.id" class="cf__dup-row">
            <a href="#" @click.prevent="$emit('open-lead', d.id)">
              {{ fullName(d) || d.company_name }}
            </a>
            <span class="cf__ltr">{{ d.mobile || d.email }}</span>
            <crm-chip :value="d.status" small />
          </div>
          <v-btn small depressed color="warning" class="mt-2" :loading="saving" @click="save(true)">
            {{ $t('crmSaveAnyway') }}
          </v-btn>
        </v-alert>
        <v-alert v-if="error" type="error" text dense>{{ error }}</v-alert>

        <!-- identity: a lead is a users row (doc §6.5) — pick an existing Abriron user or create one -->
        <div v-if="!lead" class="cf__seg">
          <button type="button" :class="{ on: source === 'new' }" @click="setSource('new')"><v-icon small>mdi-account-plus-outline</v-icon>{{ $t('crmNewPerson') }}</button>
          <button type="button" :class="{ on: source === 'existing' }" @click="setSource('existing')"><v-icon small>mdi-account-search-outline</v-icon>{{ $t('crmExistingUser') }}</button>
        </div>

        <div v-if="source === 'existing' && !lead" class="cf__grid">
          <v-autocomplete
            v-model="picked"
            :items="available"
            :loading="searching"
            :search-input.sync="userSearch"
            :label="$t('crmPickUser')"
            :no-data-text="userSearch ? $t('crmNoUserFound') : $t('crmTypeToSearch')"
            item-value="id"
            :item-text="userText"
            return-object
            no-filter
            outlined
            dense
            hide-details="auto"
            clearable
            prepend-inner-icon="mdi-magnify"
            class="cf__full"
            :error-messages="fe.user_id"
            autofocus
          >
            <template #item="{ item }">
              <div class="cf__uitem">
                <b>{{ userText(item) }}</b>
                <small><span class="cf__ltr">{{ item.mobile }}</span>{{ item.business_name ? ' · ' + item.business_name : '' }}</small>
              </div>
            </template>
          </v-autocomplete>
          <v-select v-model="f.source_id" :items="sourceItems" :label="$t('crmSource')" outlined dense hide-details="auto" clearable />
          <v-select v-model="f.status" :items="statusItems" :label="$t('crmStatus')" outlined dense hide-details="auto" />
          <v-textarea v-model="f.need" :label="$t('crmNeed')" outlined dense hide-details="auto" rows="2" auto-grow class="cf__full" />
        </div>

        <div v-else class="cf__grid">
          <v-text-field v-model="f.first_name" :label="$t('crmFirstName')" outlined dense hide-details="auto" :error-messages="fe.first_name" autofocus />
          <v-text-field v-model="f.last_name" :label="$t('crmLastName')" outlined dense hide-details="auto" :error-messages="fe.last_name" />
          <v-text-field v-model="f.company_name" :label="$t('crmCompany')" outlined dense hide-details="auto" class="cf__full" :error-messages="fe.company_name" prepend-inner-icon="mdi-domain" />
          <v-text-field v-model="f.mobile" :label="$t('crmMobile')" outlined dense hide-details="auto" dir="ltr" :error-messages="fe.mobile" prepend-inner-icon="mdi-cellphone" />
          <v-select v-model="f.source_id" :items="sourceItems" :label="$t('crmSource')" outlined dense hide-details="auto" clearable :error-messages="fe.source_id" />
          <div v-if="lead && lead.user_id" class="cf__full cf__hint"><v-icon x-small>mdi-information-outline</v-icon>{{ $t('crmIdentityInUsers') }}</div>
          <v-textarea v-model="f.need" :label="$t('crmNeed')" outlined dense hide-details="auto" rows="2" auto-grow class="cf__full" />
        </div>

        <button type="button" class="cf__more" @click="more = !more">
          <v-icon small>{{ more ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          {{ more ? $t('crmLessFields') : $t('crmMoreFields') }}
        </button>

        <div v-show="more" class="cf__grid">
          <template v-if="source === 'new' || lead">
            <v-text-field v-model="f.email" :label="$t('crmEmail')" outlined dense hide-details="auto" dir="ltr" :error-messages="fe.email" />
            <v-text-field v-model="f.phone" :label="$t('crmPhone')" outlined dense hide-details="auto" dir="ltr" />
          </template>
          <crm-user-picker v-model="f.owner_user_id" title="crmOwner" :disabled="!canAssign" />
          <v-select v-model="f.priority" :items="priorityItems" :label="$t('crmPriority')" outlined dense hide-details="auto" />
          <v-select v-if="source === 'new' || lead" v-model="f.status" :items="statusItems" :label="$t('crmStatus')" outlined dense hide-details="auto" />
          <date-picker
            v-model="f.next_follow_up_at"
            type="datetime"
            format="YYYY-MM-DD HH:mm:ss"
            display-format="jYYYY/jMM/jDD HH:mm"
            :locale="dateLocale"
            :placeholder="$t('crmNextFollowUp')"
            clearable
            class="cf__date"
          />
          <div class="cf__slider cf__full">
            <span>{{ $t('crmReadiness') }}</span>
            <v-slider v-model="f.readiness" min="0" max="100" step="10" thumb-label hide-details />
            <b>{{ f.readiness }}٪</b>
          </div>
          <div class="cf__slider cf__full">
            <span>{{ $t('crmScore') }}</span>
            <v-slider v-model="f.score" min="0" max="100" step="5" thumb-label hide-details color="success" />
            <b>{{ f.score }}</b>
          </div>
          <v-combobox v-model="f.tags" :label="$t('crmTags')" outlined dense hide-details multiple small-chips deletable-chips class="cf__full" />
          <v-textarea v-model="f.notes" :label="$t('crmNotes')" outlined dense hide-details rows="2" auto-grow class="cf__full" />
        </div>
      </div>

      <footer class="cf__foot">
        <v-btn color="primary" depressed class="app-form-action-btn" :loading="saving" @click="save(false)">
          {{ $t('crmSave') }}
        </v-btn>
        <v-btn color="grey" outlined class="app-form-action-btn" @click="close">{{ $t('crmCancel') }}</v-btn>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
import CrmUserPicker from './CrmUserPicker'
import api, { crmError } from '../crmApi'
import { crm, can, PRIORITIES, fullName, personName, leadCompany, leadPhone, leadEmail, leadStatuses, pick } from '../crmStore'
import { CrmChip } from './CrmBits'

function defaultStatus () {
  const list = leadStatuses()
  const d = list.find(s => s.is_default) || list.find(s => s.key === 'new') || list[0]
  return d ? d.key : 'new'
}

function blank () {
  return {
    first_name: '',
    last_name: '',
    company_name: '',
    mobile: '',
    phone: '',
    email: '',
    source_id: null,
    need: '',
    notes: '',
    owner_user_id: crm.me ? crm.me.user_id : null,
    status: defaultStatus(),
    priority: 'normal',
    readiness: 0,
    score: 0,
    tags: [],
    next_follow_up_at: ''
  }
}

export default {
  name: 'CrmLeadForm',
  components: { CrmUserPicker, CrmChip },
  props: { value: Boolean, lead: { type: Object, default: null }, prefill: { type: Object, default: null } },
  data: () => ({
    f: blank(),
    fe: {},
    error: '',
    duplicates: [],
    saving: false,
    more: false,
    source: 'new',
    picked: null,
    available: [],
    userSearch: '',
    searching: false,
    timer: null
  }),
  computed: {
    sourceItems () { return crm.sources.map(s => ({ value: s.id, text: s.title })) },
    priorityItems () { return PRIORITIES.map(p => ({ value: p, text: this.$t('crmPriority_' + p) })) },
    statusItems () {
      // converted is set by the convert action only
      return leadStatuses().filter(s => s.key !== 'converted').map(s => ({ value: s.key, text: s.title || this.$t('crmStatus_' + s.key) }))
    },
    canAssign () { return can('leads.assign') },
    dateLocale () { return this.$i18n.locale === 'en' ? 'en' : 'fa' }
  },
  watch: {
    value: { immediate: true, handler (v) { if (v) this.reset() } },
    userSearch (q) {
      clearTimeout(this.timer)
      if (this.picked && q === this.userText(this.picked)) return
      this.timer = setTimeout(() => this.searchUsers(q), 350)
    }
  },
  methods: {
    fullName,
    userText (u) { return (u && (personName(u) || u.name || u.business_name || u.mobile)) || '' },
    setSource (s) {
      this.source = s
      this.fe = {}
      if (s === 'existing' && !this.available.length) this.searchUsers('')
    },
    async searchUsers (q) {
      this.searching = true
      try {
        const res = await api.leadsAvailable({ search: q || undefined, itemsPerPage: 30 })
        this.available = pick(res)
      } catch (e) { this.available = [] } finally { this.searching = false }
    },
    reset () {
      const f = blank()
      const src = this.lead || this.prefill
      if (src) Object.keys(f).forEach(k => { if (src[k] !== undefined && src[k] !== null) f[k] = src[k] })
      if (this.lead) {
        // identity lives in users; fill the form from the joined user
        const u = this.lead.user || {}
        f.first_name = f.first_name || u.first_name || u.firstname || ''
        f.last_name = f.last_name || u.last_name || u.lastname || ''
        f.company_name = leadCompany(this.lead)
        f.mobile = leadPhone(this.lead)
        f.email = leadEmail(this.lead)
      }
      this.source = 'new'
      this.picked = null
      f.tags = Array.isArray(f.tags) ? f.tags.slice() : []
      this.f = f
      this.fe = {}
      this.error = ''
      this.duplicates = []
      this.more = !!this.lead
    },
    close () { this.$emit('input', false) },
    async save (force) {
      const existing = this.source === 'existing' && !this.lead
      if (existing && !this.picked) {
        this.fe = { user_id: this.$t('crmPickUserRequired') }
        return
      }
      if (!existing && !this.f.first_name && !this.f.last_name && !this.f.company_name) {
        this.fe = { first_name: this.$t('crmNameOrCompanyRequired') }
        return
      }
      this.saving = true
      this.error = ''
      this.fe = {}
      const f = this.f
      const body = existing
        ? {
          user_id: this.picked.id,
          source_id: f.source_id,
          status: f.status,
          need: f.need,
          owner_user_id: f.owner_user_id,
          priority: f.priority,
          tags: f.tags,
          notes: f.notes,
          next_follow_up_at: f.next_follow_up_at,
          readiness: f.readiness,
          score: f.score,
          force: !!force
        }
        : { ...f, force: !!force }
      if (!body.next_follow_up_at) body.next_follow_up_at = null
      try {
        const res = this.lead ? await api.updateLead(this.lead.id, body) : await api.createLead(body)
        this.$emit('saved', res && res.lead ? res.lead : res)
        this.close()
      } catch (e) {
        const err = crmError(e)
        if (err.code === 'duplicate') {
          this.duplicates = err.payload.duplicates || []
        } else {
          this.fe = err.fields
          this.error = err.message
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.cf { display: flex; flex-direction: column; background: #fff; border-radius: 6px; max-height: 90vh; overflow: hidden; }
.theme--dark .cf { background: #1e1e1e; }
.cf__head { display: flex; align-items: center; gap: 12px; padding: 14px 18px; border-bottom: 1px solid rgba(15, 23, 42, .07); flex-shrink: 0; }
.cf__icon { width: 40px; height: 40px; border-radius: 6px; background: rgba(25, 118, 210, .1); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.cf__titles { flex: 1; min-width: 0; }
.cf__title { font-weight: 700; font-size: 1rem; }
.cf__sub { font-size: .78rem; opacity: .6; }
.cf__close { width: 36px; height: 36px; border-radius: 6px; background: rgba(0, 0, 0, .05); display: flex; align-items: center; justify-content: center; }
.cf__body { padding: 20px 18px; overflow-y: auto; overflow-x: hidden; flex: 1; }
.cf__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 22px 14px; }
.cf__full { grid-column: 1 / -1; }
.cf__more { display: inline-flex; align-items: center; gap: 4px; margin: 16px 0; font-size: .82rem; font-weight: 600; color: var(--v-primary-base); }
.cf__slider { display: flex; align-items: center; gap: 12px; font-size: .82rem; }
.cf__slider span { min-width: 90px; opacity: .7; }
.cf__slider b { min-width: 36px; text-align: end; }
.cf__foot { display: flex; gap: 10px; padding: 12px 18px; background: #fafbfd; border-top: 1px solid rgba(15, 23, 42, .07); flex-shrink: 0; }
.theme--dark .cf__foot { background: #232323; }
.cf__seg { display: flex; padding: 3px; background: rgba(127, 127, 127, .1); border-radius: 6px; margin-bottom: 18px; }
.cf__seg button { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px; height: 34px; border-radius: 6px; font-size: .84rem; font-weight: 600; opacity: .65; }
.cf__seg button.on { background: #fff; opacity: 1; color: var(--v-primary-base); box-shadow: 0 1px 3px rgba(15, 23, 42, .12); }
.theme--dark .cf__seg button.on { background: #333; }
.cf__uitem { display: flex; flex-direction: column; padding: 4px 0; line-height: 1.4; }
.cf__uitem small { opacity: .65; }
.cf__hint { display: flex; align-items: center; gap: 6px; font-size: .74rem; opacity: .65; margin-top: -10px; }
.cf__dup-title { font-weight: 700; margin-bottom: 6px; }
.cf__dup-row { display: flex; align-items: center; gap: 10px; padding: 3px 0; font-size: .85rem; }
.cf__ltr { direction: ltr; opacity: .7; }
.cf ::v-deep .v-input { height: auto !important; margin: 0; padding: 0; }
.cf ::v-deep .v-input fieldset { border-radius: 6px; }
.cf__date ::v-deep .vpd-input-group input { height: 40px; border-radius: 0 6px 6px 0; }
@media (max-width: 799px) {
  .cf { max-height: none; height: 100%; border-radius: 0; }
  .cf__grid { grid-template-columns: 1fr; }
}
</style>
